import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useUpdateOrganization } from "@via/pages/organization/organization.api.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import {
  maxLengthValidation,
  requiredValidation,
} from "@via/utils/form-validators.tsx";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";

interface OrganizationUpdateFormPayload {
  name: string;
  description: string;
}

interface OrganizationUpdateFormProps {
  onUpdateSuccess: () => void;
  organizationId: string;
  isEditMode?: boolean;
  defaultValues?: OrganizationUpdateFormPayload;
  isDisabled?: boolean;
  onEdit?: () => void;
  onboardingStep?: string;
}

const OrganizationUpdateForm: React.FC<OrganizationUpdateFormProps> = ({
  onUpdateSuccess,
  organizationId,
  isEditMode = false,
  defaultValues,
  isDisabled = false,
  onEdit,
  onboardingStep,
}) => {
  const { handleSubmit, control } = useForm<OrganizationUpdateFormPayload>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      name: defaultValues?.name || "",
      description: defaultValues?.description || "",
    },
  });

  const {
    mutate: updateOrganization,
    isPending: isUpdateOrganizationPending,
    isSuccess: isUpdateOrganizationSuccess,
  } = useUpdateOrganization();

  useEffect(() => {
    if (isUpdateOrganizationSuccess) {
      onUpdateSuccess();
    }
  }, [isUpdateOrganizationSuccess]);

  const handleFormSubmit = async (payload: OrganizationUpdateFormPayload) => {
    updateOrganization({
      requestParams: { id: organizationId, onboardingStep },
      requestBody: payload,
    });
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleFormSubmit)(e);
      }}>
      <FormInputComponent
        control={control}
        name="name"
        label="Name"
        showRequiredField={true}
        placeholder="Enter a name"
        validations={{
          ...requiredValidation("Name"),
          ...maxLengthValidation(250, "Name must be less than 64 characters"),
        }}
        disabled={isDisabled}
      />
      <FormTextAreaComponent
        control={control}
        name="description"
        label="Description"
        placeholder="Add a description"
        rows={5}
        validations={maxLengthValidation(
          1000,
          "Description must be less than 1000 characters"
        )}
        disabled={isDisabled}
      />
      <div className="flex flex-shrink-0 justify-end">
        {isEditMode ? (
          isDisabled ? (
            <button
              type="button"
              disabled={isUpdateOrganizationPending}
              onClick={() => onEdit?.()}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Edit
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={isUpdateOrganizationPending}
                onClick={() => onEdit?.()}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-2 mr-2">
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdateOrganizationPending}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Update
              </button>
            </>
          )
        ) : (
          <button
            type="submit"
            disabled={isUpdateOrganizationPending || isDisabled}
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            <Spinner loading={isUpdateOrganizationPending}>Next</Spinner>
          </button>
        )}
      </div>
    </form>
  );
};

export default OrganizationUpdateForm;
